// frontend/src/hooks/useUpgradeOffer.js

import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { getUpgradeOffer } from "../services/payments";
import { hasReferralDiscountAhead } from "../utils/friendCodeOffer";

/**
 * The signed-in user's current upgrade offer, shared by every selling surface.
 *
 * THREE CONSUMERS, ONE REQUEST. The navbar pill, the dashboard banner and the
 * checkout all render the same offer and commonly mount together (a trialing
 * user landing on /dashboard mounts two of them in the same tick). A per-
 * component fetch would issue two or three identical requests on every route
 * change, so the cache and the in-flight promise live at MODULE level, not in
 * component state: the second and third caller await the first one's promise.
 *
 * WHY A TTL AND NOT A ONE-SHOT. The offer decays - its percent is
 * `1.5% x whole trial days left`, so it drops by one step every 24h, and the
 * price it quotes is the price the checkout will charge. 60 s is short enough
 * that a long-lived tab cannot show a stale percent for meaningfully longer
 * than a page load, and long enough that navigating dashboard -> payment ->
 * back costs one request, not three.
 *
 * FAIL-CLOSED, EVERYWHERE. A rejected request resolves to `offer === null` plus
 * an `error`; it never throws into a render, and it never leaves a previous
 * offer on screen. Every consumer renders its offer affordance only on an
 * affirmative `eligible === true`, so an outage silently returns all three
 * surfaces to their pre-offer appearance rather than showing a stale or
 * half-built price.
 *
 * NEVER CALLED FROM A LIST ITEM. This is a per-user singleton read; the only
 * legitimate callers are the three chrome-level surfaces named above.
 */

/**
 * How long a settled result is reused. See the note above - this bounds how
 * stale a displayed percent can be, so it is a correctness knob, not a perf one.
 */
export const UPGRADE_OFFER_TTL_MS = 60 * 1000;

/**
 * How long one offer read may take before it counts as FAILED (R2F-03).
 *
 * The checkout holds its plan buttons until the offer settles (display ==
 * charge), so a request that never settles - a cold instance, a stalled mobile
 * socket - would otherwise hold the main conversion surface forever. The
 * survey's 8 s bound is the precedent. A timed-out read is an ordinary failure:
 * no offer, `error` set, and every surface falls back exactly as on a 5xx.
 */
export const UPGRADE_OFFER_TIMEOUT_MS = 8000;

/**
 * The checkout's retry schedule for a REFERRED customer whose offer read
 * failed: 2 s, 4 s, 8 s, ... capped at 30 s. Only the checkout retries (see
 * `includeReferredPaid`) - it is the one surface that must not let a referred
 * customer pay without the referral price on screen.
 */
export const REFERRAL_RETRY_BASE_MS = 2000;
export const REFERRAL_RETRY_MAX_MS = 30000;

/**
 * How many AUTOMATIC retries a referred checkout makes before it stops (L-03).
 *
 * The backoff alone never ends: a referred customer who left /payment open
 * through an outage would send one offer read every 30 s for as long as the
 * tab lives. Ten retries is roughly 3.5 minutes of waiting (2+4+8+16 s, then
 * 30 s steps) plus up to `UPGRADE_OFFER_TIMEOUT_MS` per read - about five
 * minutes of failures in the worst case. After that the buttons STAY held
 * (the price is still unknown) and the checkout offers a manual retry
 * (`retryReferralPrice`) instead of polling on its own.
 */
export const REFERRAL_RETRY_MAX_ATTEMPTS = 10;

/** @param {number} failures consecutive failed reads so far (>= 1) */
export const referralRetryDelay = (failures) =>
  Math.min(REFERRAL_RETRY_BASE_MS * 2 ** Math.max(failures - 1, 0), REFERRAL_RETRY_MAX_MS);

/** Raised when the offer read outlives `UPGRADE_OFFER_TIMEOUT_MS`. */
export class UpgradeOfferTimeoutError extends Error {
  constructor() {
    super("upgrade offer request timed out");
    this.name = "UpgradeOfferTimeoutError";
  }
}

/**
 * `promise`, or a rejection with `UpgradeOfferTimeoutError` after `ms`.
 * The timer is cleared the moment the request settles either way.
 */
const withTimeout = (promise, ms) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new UpgradeOfferTimeoutError()), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

/**
 * The module-level cache: at most ONE entry, for one (user, token) pair.
 *
 * Shape: `{ userId, token, fetchedAt, promise, value, error }`.
 *  - `promise` non-null  => a request is in flight; new callers join it.
 *  - `value` non-null    => the last successful payload.
 *  - `error` non-null    => the last attempt failed (and `value` is null).
 *
 * A single entry is deliberate: two identities can never be cached at once, so
 * there is no way for user A's price to be handed to user B. A key mismatch
 * drops the entry outright.
 */
let cache = null;

/**
 * The user id a friend code was JUST applied for in this tab, or null.
 *
 * `POST /users/me/partner-code` answering "attributed" is the server's word
 * that this account is now priced by the referral - but the `user` payload
 * only learns it when `refreshUser()` lands, and that refresh swallows every
 * error. Without this hint a LAPSED account (admitted to an offer only by its
 * payload) would see full prices on the cards after the apply and then be
 * charged the referral price - display != charge. The hint admits the user
 * from the apply response alone; it lives for the page's lifetime (a payment
 * leaves through the gateway, which reloads the app) and is dropped with the
 * cache on logout / user switch.
 */
let referralHintUserId = null;

/**
 * The user id whose friend-code apply had an UNKNOWN outcome in this tab, or
 * null (L-01).
 *
 * The apply's answer was lost (5xx / network - the server commits the
 * attribution before it answers) AND the payload refresh that would have told
 * us failed too. The account may or may not be referred now, and only the
 * server knows - so it is admitted to the checkout's offer read exactly like a
 * referred one: the read's answer prices the cards (a referral offer, or
 * `eligible=false` at the list price, both settle it), and a FAILED read keeps
 * the plan buttons held behind the referral retry rather than releasing them at
 * a list price the server may no longer charge. Same lifetime as
 * `referralHintUserId`.
 */
let unconfirmedReferralUserId = null;

/**
 * Drop the cached offer. Called on logout / user switch, and by tests.
 *
 * Exported because a stale offer is a stale PRICE: an account switch inside one
 * tab must not let the previous user's committed price or discount paint for
 * even one frame.
 */
export const clearUpgradeOfferCache = () => {
  cache = null;
  referralHintUserId = null;
  unconfirmedReferralUserId = null;
};

/**
 * Mounted hooks that must re-read when the offer is INVALIDATED (not merely
 * cleared): a friend code applied at checkout changes this user's price NOW,
 * and every surface on screen - the plan cards, the navbar chip, the banner -
 * must show the new one without waiting out the TTL or a remount.
 */
const invalidationListeners = new Set();

/**
 * Drop the cached offer AND make every mounted consumer fetch it again.
 *
 * Called after a friend code is applied (the server now prices this user by
 * the referral). The consumers re-run through the normal single-flight path,
 * so three mounted surfaces still cost ONE request, and each one DROPS the
 * offer it was showing the moment it re-runs - a price the server no longer
 * charges is never left on screen while the new one is in flight.
 *
 * @param {object} [options]
 * @param {string|number|null} [options.referredUserId] the account a code was
 *   just applied for - admits it to the offer before its payload catches up
 *   (see `referralHintUserId`).
 * @param {string|number|null} [options.unconfirmedUserId] the account whose
 *   apply outcome is UNKNOWN (answer lost, payload refresh failed) - admits it
 *   to the checkout's offer read and its held retry (see
 *   `unconfirmedReferralUserId`).
 */
export const invalidateUpgradeOffer = ({ referredUserId = null, unconfirmedUserId = null } = {}) => {
  cache = null;
  if (referredUserId !== null && referredUserId !== undefined) {
    referralHintUserId = referredUserId;
  }
  if (unconfirmedUserId !== null && unconfirmedUserId !== undefined) {
    unconfirmedReferralUserId = unconfirmedUserId;
  }
  invalidationListeners.forEach((notify) => {
    try {
      notify();
    } catch (error) {
      /* a consumer that unmounted mid-loop must not stop the others */
    }
  });
};

/** Does the cache hold a settled, still-fresh entry for this exact key? */
const readFresh = (userId, token, now) => {
  if (!cache) return null;
  if (cache.userId !== userId || cache.token !== token) return null;
  if (cache.promise) return null;
  if (now - cache.fetchedAt >= UPGRADE_OFFER_TTL_MS) return null;
  return cache;
};

/**
 * Resolve the offer for one (user, token) pair, sharing work across callers.
 *
 * `force` bypasses the TTL but NOT the in-flight promise: joining a request
 * that is already on the wire is strictly better than racing a second one
 * against it, and the checkout (the only forcing caller) gets the same fresh
 * answer either way.
 */
const loadOffer = (userId, token, { force }) => {
  const now = Date.now();

  if (cache && (cache.userId !== userId || cache.token !== token)) {
    cache = null;
  }
  if (cache) {
    if (cache.promise) return cache.promise;
    if (!force && now - cache.fetchedAt < UPGRADE_OFFER_TTL_MS) {
      return Promise.resolve(cache);
    }
  }

  const entry = {
    userId,
    token,
    fetchedAt: now,
    promise: null,
    value: null,
    error: null,
  };

  // A SYNCHRONOUS throw from the service layer is the same class of failure as
  // a rejected request, and until it was contained here it escaped the whole
  // fail-closed contract: the exception surfaced inside the effect and took
  // down the page the hook was only supposed to decorate. `Promise.resolve` on
  // a native promise returns that same promise, so the request is still issued
  // synchronously at this line - which is what makes the single-flight join
  // below work for a second consumer mounting in the same tick.
  //
  // Bounded by `UPGRADE_OFFER_TIMEOUT_MS`: a read that hangs settles as a
  // failure instead of holding the checkout's buttons forever.
  let request;
  try {
    request = withTimeout(Promise.resolve(getUpgradeOffer(token)), UPGRADE_OFFER_TIMEOUT_MS);
  } catch (error) {
    request = Promise.reject(error);
  }

  entry.promise = request
    .then((data) => {
      entry.value = data;
      entry.error = null;
    })
    .catch((error) => {
      // Swallowed on purpose: an unreachable offer endpoint must degrade the
      // three selling surfaces, never break the dashboard they sit on.
      entry.value = null;
      entry.error = error;
    })
    .then(() => {
      entry.promise = null;
      // Re-stamped at SETTLE time, not at request time: a request that took
      // 20 s would otherwise be two thirds expired the moment it arrived.
      entry.fetchedAt = Date.now();
      return entry;
    });

  cache = entry;
  return entry.promise;
};

const EMPTY_STATE = { offer: null, loading: false, error: null };

/**
 * Drop a FAILED cache entry so the next read goes to the network even inside
 * the TTL (a retry must not be answered by the failure it is retrying).
 */
const dropFailedEntry = () => {
  if (cache && !cache.promise && cache.error) cache = null;
};

/**
 * @param {object} [options]
 * @param {boolean} [options.force] bypass the TTL on mount (the checkout does,
 *   because the number on that page is the number about to be charged).
 * @param {string|null} [options.winbackToken] token from the comeback email.
 *   Its presence also admits an `inactive` user, who is otherwise not offered
 *   anything in-app.
 * @param {boolean} [options.includeReferredPaid] admit a REFERRED account in
 *   its active / lapsed states too. Only the checkout passes it: the navbar
 *   chip and the dashboard banner render an offer for trialing users alone,
 *   so reading it for a paying referred customer there would cost one
 *   request per page for nothing.
 * @returns {{offer: object|null, loading: boolean, error: Error|null,
 *   referralPriceUnavailable: boolean, referralPriceStalled: boolean,
 *   retryReferralPrice: () => void}}
 *   `referralPriceUnavailable` (checkout only) is true while a REFERRED
 *   customer's offer read has failed and is being retried: the caller must
 *   keep its plan buttons held and say the price is still loading. Everyone
 *   else falls back to the list price on a failure, as before.
 *   `referralPriceStalled` is true once the automatic retries are spent
 *   (`REFERRAL_RETRY_MAX_ATTEMPTS`): the price is still unavailable (buttons
 *   still held), nothing polls any more, and `retryReferralPrice` starts a
 *   fresh attempt on the customer's request.
 */
export default function useUpgradeOffer({
  force = false,
  winbackToken = null,
  includeReferredPaid = false,
} = {}) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const token = winbackToken || null;

  // Who is even offered something. Admins are excluded on every surface (they
  // are not billed), and a plain `inactive` user is only in scope when they
  // arrived on a win-back link - that is the one path that can still discount.
  //
  // FRIEND CODE (partner program): a REFERRED user with discounted charges
  // still ahead is priced by the referral in EVERY lifecycle state the
  // checkout serves - trialing, active (a Solo -> Professional upgrade inside
  // the two discounted charges) and lapsed - so the CHECKOUT reads the offer
  // for them in all three (`includeReferredPaid`). Without this an active or
  // lapsed referred customer would see full prices on the cards and then be
  // charged the referral price. A code applied a moment ago in this tab counts
  // as referred before the payload says so (`referralHintUserId`).
  //
  // An apply whose outcome is UNKNOWN (L-01, `unconfirmedReferralUserId`) is
  // admitted the same way: the server's offer answer settles it either way.
  const referred =
    hasReferralDiscountAhead(user) ||
    (userId !== null &&
      (referralHintUserId === userId || unconfirmedReferralUserId === userId));
  const referralAhead =
    includeReferredPaid && referred && ["trial", "active", "inactive"].includes(user?.status);
  const eligible = Boolean(
    userId &&
      !user?.is_admin &&
      (user?.status === "trial" ||
        (token && user?.status === "inactive") ||
        referralAhead)
  );

  // THE FIRST RENDER ALREADY KNOWS (review FE-4). An eligible caller with no
  // fresh cache entry starts as `loading` - not as the empty state the fetch
  // effect only replaces AFTER the first paint - so no surface paints one
  // frame of "no offer" (the Billing row's "Upgrade now" for a customer who
  // holds a scheduled charge). A fresh cache hit starts settled.
  const [state, setState] = useState(() => {
    if (!eligible) return EMPTY_STATE;
    const hit = force ? null : readFresh(userId, token, Date.now());
    return hit
      ? { offer: hit.value, loading: false, error: hit.error }
      : { offer: null, loading: true, error: null };
  });

  // Bumped by `invalidateUpgradeOffer`; part of the fetch effect's deps, so an
  // invalidation re-runs it and bypasses the (now empty) cache.
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    const notify = () => setGeneration((n) => n + 1);
    invalidationListeners.add(notify);
    return () => {
      invalidationListeners.delete(notify);
    };
  }, []);

  // Consecutive failed reads for a referred checkout (0 = none / succeeded).
  const [referralFailures, setReferralFailures] = useState(0);
  // The automatic retries are spent (L-03) - see `REFERRAL_RETRY_MAX_ATTEMPTS`.
  const [referralGaveUp, setReferralGaveUp] = useState(false);
  const retryReferral = referralAhead;

  useEffect(() => {
    if (!userId) {
      // Logged out (or logged out and back in as someone else): the previous
      // identity's price must not survive in memory.
      clearUpgradeOfferCache();
    }
    if (!eligible) {
      setState(EMPTY_STATE);
      return undefined;
    }

    const hit = force ? null : readFresh(userId, token, Date.now());
    if (hit) {
      // Synchronous cache hit - set state without a loading frame, so a
      // navbar that already knows the saving does not blink back to the
      // plain label on every route change.
      setState({ offer: hit.value, loading: false, error: hit.error });
      return undefined;
    }

    let cancelled = false;
    // DROP whatever was on screen. A re-run means the key changed (another
    // user, token or eligibility) or the offer was INVALIDATED (a friend code
    // was just applied) - in every case the previous offer may quote a price
    // the server will no longer charge, so it must not stay painted while the
    // new one is in flight. Surfaces read `loading` to hold their CTAs.
    setState({ offer: null, loading: true, error: null });

    loadOffer(userId, token, { force }).then((entry) => {
      if (cancelled) return;
      setState({ offer: entry.value, loading: false, error: entry.error });
    });

    return () => {
      cancelled = true;
    };
  }, [eligible, userId, token, force, generation]);

  // A REFERRED customer's checkout never gives up on the price (R2F-03 /
  // decision F3): a failed or timed-out read is retried with backoff, and the
  // caller holds its buttons meanwhile. A settled answer - any offer, or none -
  // ends the retrying; so does leaving the referred state.
  const failedNow = retryReferral && !state.loading && Boolean(state.error);
  //
  // The retrying is BOUNDED (L-03): after `REFERRAL_RETRY_MAX_ATTEMPTS`
  // automatic retries it stops scheduling - the price is still unknown, so the
  // buttons stay held, and the customer gets a manual retry instead.
  useEffect(() => {
    if (!retryReferral) {
      setReferralFailures(0);
      setReferralGaveUp(false);
      return undefined;
    }
    if (state.loading) return undefined;
    if (!state.error) {
      setReferralFailures(0);
      setReferralGaveUp(false);
      return undefined;
    }
    const failures = referralFailures + 1;
    if (failures > REFERRAL_RETRY_MAX_ATTEMPTS) {
      setReferralGaveUp(true);
      return undefined;
    }
    const timer = setTimeout(() => {
      dropFailedEntry();
      setReferralFailures(failures);
      setGeneration((n) => n + 1);
    }, referralRetryDelay(failures));
    return () => clearTimeout(timer);
    // `referralFailures` is read, not a trigger: the next attempt is scheduled
    // by the next settled failure, never by the counter itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryReferral, state]);

  const referralPriceUnavailable = Boolean(
    retryReferral && (failedNow || (referralFailures > 0 && state.loading))
  );
  // Only while the last read is a settled failure: a re-read started by an
  // invalidation or by `retryReferralPrice` is "loading" again, not stalled.
  const referralPriceStalled = Boolean(failedNow && referralGaveUp);

  /**
   * The customer asked to try again after the automatic retries stopped.
   * One fresh network read (a failed cache entry never answers a retry); a
   * failure from here re-enters the backoff from its second step, so it is
   * bounded the same way.
   */
  const retryReferralPrice = useCallback(() => {
    dropFailedEntry();
    setReferralGaveUp(false);
    setReferralFailures(1);
    setGeneration((n) => n + 1);
  }, []);

  return useMemo(
    () => ({ ...state, referralPriceUnavailable, referralPriceStalled, retryReferralPrice }),
    [state, referralPriceUnavailable, referralPriceStalled, retryReferralPrice]
  );
}
